import { FormControl, FormHelperText, FormLabel, Typography } from '@healthinal/ui';
import { FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { AddressDtoAddressType } from '../../../api/generated/model';
import { GermanDateInput } from '../../../components/form/GermanDateInput.tsx';
import { InstitutionContactFields } from '../../../components/form/InstitutionContactFields.tsx';
import { getDateAfterInputValue } from '../../../utils/date.ts';

interface CreatePatientAddressFormProps<TFieldVales extends FieldValues> {
  from: FieldPath<TFieldVales>;
  until: FieldPath<TFieldVales>;
  address: FieldPath<TFieldVales>;
  contact: FieldPath<TFieldVales>;
  fromInputName: string;
  untilInputName: string;
  addressInputName: string;
  contactInputName: string;
  addressTypes: AddressDtoAddressType[];
}
export function CreatePatientAddressForm<TFieldValues extends FieldValues>({
  from,
  until,
  address,
  contact,
  fromInputName,
  untilInputName,
  addressInputName,
  contactInputName,
  addressTypes,
}: CreatePatientAddressFormProps<TFieldValues>) {
  const formMethods = useFormContext<TFieldValues>();
  const {
    control,
    watch,
    formState: { errors },
    getFieldState,
  } = formMethods;

  const fromValue = watch(from);
  const untilMinDate = typeof fromValue === 'string' ? getDateAfterInputValue(fromValue) : undefined;
  const addressFieldState = getFieldState(address);

  return (
    <>
      <FormControl error={!!errors.from}>
        <FormLabel>
          <Typography>{fromInputName} *</Typography>
        </FormLabel>
        <GermanDateInput
          control={control}
          name={from}
          rules={{
            required: true,
          }}
        />
        {errors.from && <FormHelperText>{fromInputName} muss ausgefüllt werden</FormHelperText>}
      </FormControl>
      <FormControl error={!!errors.until}>
        <FormLabel>
          <Typography>{untilInputName}</Typography>
        </FormLabel>
        <GermanDateInput
          control={control}
          name={until}
          rules={{
            required: false,
            ...(untilMinDate ? { min: { value: untilMinDate, message: 'range' } } : {}),
          }}
          min={untilMinDate}
        />
        {errors.until && (
          <FormHelperText>
            {untilInputName} muss nach {fromInputName} liegen
          </FormHelperText>
        )}
      </FormControl>
      <InstitutionContactFields
        formMethods={formMethods}
        layout="fragment"
        addressField={address}
        contactField={contact}
        addressLabel={`${addressInputName} *`}
        contactLabel={contactInputName}
        addressTypes={addressTypes}
        canChangeAddressType={addressTypes.length > 1}
        addressRules={{ required: true }}
        addressError={!!addressFieldState.error}
        addressHelperText={addressFieldState.error ? `${addressInputName} muss angegeben werden` : undefined}
        clearContactOnAddressChange="always"
        contactClearOptions={false}
        useAllContactsList={true}
      />
    </>
  );
}
